import type { Option, Timescale } from "@tento-lona/sheets";
import { HeaderLegendLayout } from "./header/support/header-plugin.legend-layout.ts";
import type { TextMeasurer } from "@tento-lona/sheets-ui/sheet/default-legend-layout.ts";
import { NaiveDate } from "@tento-chrono";
import { LonaIcons, NumberIcon } from "@tento-ui/ui/icons";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import {
  type BindCellContext,
  type RenderPlugin,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { HeaderLegend } from "./header/support/legend.ts";
import { ColumnHeader } from "../shared/column-header/cell.ts";
import { $$ } from "@tento-ui/fastdom";
import { headerCellPaddingInline } from "../shared/column-header/span-range.ts";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { DateFormat, type FirstDay } from "@tento-lona";
import { HEADER_INERT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";

/** The inert-row plugin for a sheet's column header row. */
export class HeaderInertPlugin
  implements RenderPlugin.Inert<ColumnHeader, HeaderLegend>
{
  static readonly spec: PerTypeSpec = HEADER_INERT_SPEC;

  readonly role = "inert";
  readonly id = "header";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(NumberIcon);

  #dateFormat: LiveData.Readable<DateFormat>;
  #firstDay: LiveData.Readable<FirstDay>;
  #onSelectHeader: Option<(d: NaiveDate.Partial.WithTz) => void>;
  constructor(
    onSelectHeader: Option<(d: NaiveDate.Partial.WithTz) => void>,
    firstDay: LiveData.Readable<FirstDay> = new LiveData<FirstDay>(
      "monday",
    ),
    dateFormat: LiveData.Readable<DateFormat> = new LiveData<DateFormat>(
      "month-day",
    ),
  ) {
    this.#onSelectHeader = onSelectHeader;
    this.#firstDay = firstDay;
    this.#dateFormat = dateFormat;
  }

  makeCell(): ColumnHeader {
    return ColumnHeader.make();
  }

  onResize($cell: ColumnHeader, context: BindCellContext): void {
    this.bindCell($cell, context);
  }

  async bindCell(
    $cell: ColumnHeader,
    { column, isCurrent }: BindCellContext,
  ) {
    $$.mutate(() => {
      const c = column.discrete();
      $cell.bind(
        c,
        isCurrent ? "current" : "other",
        this.#onSelectHeader?.bind(this) ?? null,
        this.#dateFormat.get(),
      );

      $cell.assignStyles({ paddingInline: headerCellPaddingInline(c[0].type) });
    });
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeRowOptions = null;

  loadData = null;

  legendFactory: SheetLegendClass<HeaderLegend> = ((_self) =>
    class {
      static make(): HeaderLegend {
        return HeaderLegend.make();
      }

      static bind(
        _$l: HeaderLegend,
        _row: SheetRow.Joined.Handle,
        _handle: SheetDelegate,
        _sheetTimescale: Timescale,
      ) {}

      static computeWidth(_row: SheetRow.Joined.Handle, measure: TextMeasurer): number {
        return HeaderLegendLayout.width(measure);
      }
    })(this);

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}

  onViewportColumnsChanged(
    _row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    _sheet: SheetDelegate,
    $legend: Option<HeaderLegend>,
  ): void {
    const nd = columns.start.pd.start;
    if (!$legend) {
      // No legend element to paint the month/year into (legend rebuilt or never
      // built since this hook last ran) — the header band stays as it was.
      Tracing.log("header/legend-missing", {
        month: nd.month.name,
        year: String(nd.yr),
      });
      return;
    }
    $legend.bind(nd);
    Tracing.log("header/legend-bind", {
      month: nd.month.name,
      year: String(nd.yr),
    });
  }

  onRenderedColumnsWillChange(): void {}
}
